import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './redux/store';
import { App as AntApp, ConfigProvider, Spin, theme as antTheme } from 'antd';
import ProtectedRoute from './navigation/protectedRoute';
import AppLayout from './navigation/AppLayout';
import { useTheme } from './hooks/useTheme';
import './App.css';

// Guard: coordinators must select a fest before accessing fest-specific pages
const CoordinatorFestGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const selectedCoordinatorFest = useSelector((state: RootState) => (state as any).Coordinator?.selectedFest ?? null);
  const user = useSelector((state: RootState) => (state as any).User?.user);
  if (user?.role !== 'ITEM_COORDINATOR') return <>{children}</>;
  if (!selectedCoordinatorFest) {
    return <Navigate to="/coordinator/fests" state={{ forceSelect: true }} replace />;
  }
  return <>{children}</>;
};

// Guard: volunteers must select a fest before accessing fest-specific pages
const VolunteerFestGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const selectedVolunteerFest = useSelector((state: RootState) => (state as any).Volunteer?.selectedFest ?? null);
  const user = useSelector((state: RootState) => (state as any).User?.user);
  if (user?.role !== 'VOLUNTEER') return <>{children}</>;
  if (!selectedVolunteerFest) {
    return <Navigate to="/volunteer/fests" state={{ forceSelect: true }} replace />;
  }
  return <>{children}</>;
};

// Guard: custom role users must select a fest before accessing fest-scoped pages
// Public paths that don't need a fest selected
const CUSTOM_ROLE_PUBLIC_PATHS = new Set(['dashboard', 'fests', 'templates', 'certificate-templates', 'poster-templates', 'users', 'settings', 'profile', 'reports']);
const CustomRoleFestGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const selectedFest = useSelector((state: RootState) => (state as any).CustomRole?.selectedFest ?? null);
  const user = useSelector((state: RootState) => (state as any).User?.user);
  if (user?.role !== 'CUSTOM') return <>{children}</>;
  // Get the path segment after the role slug (e.g. /creator/events → 'events')
  const pathParts = location.pathname.split('/').filter(Boolean);
  const segment = pathParts[1] || 'dashboard'; // pathParts[0] = role slug
  if (!selectedFest && !CUSTOM_ROLE_PUBLIC_PATHS.has(segment)) {
    const roleName = user?.roleName || 'custom';
    const slug = roleName.toLowerCase().replace(/\s+/g, '-');
    return <Navigate to={`/${slug}/fests`} state={{ forceSelect: true }} replace />;
  }
  return <>{children}</>;
};

// Guard: team leads must select a fest before accessing fest-specific pages
// Public paths (no fest required): my-fests, announcements, settings, profile
const TEAM_LEAD_PUBLIC_PATHS = new Set(['my-fests', 'dashboard', 'announcements', 'settings', 'profile']);
const TeamLeadFestGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const selectedAssignment = useSelector((state: RootState) => (state as any).TeamLead?.selectedAssignment ?? null);
  const user = useSelector((state: RootState) => (state as any).User?.user);
  if (user?.role !== 'TEAM_LEAD') return <>{children}</>;
  const segment = location.pathname.split('/team-lead/')[1]?.split('/')[0] || '';
  if (!selectedAssignment && !TEAM_LEAD_PUBLIC_PATHS.has(segment)) {
    return <Navigate to="/team-lead/my-fests" replace />;
  }
  return <>{children}</>;
};

// Lazy-loaded pages — only loaded when navigated to
const Login = lazy(() => import('./pages/auth/Login'));
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const RegisterInstitution = lazy(() => import('./pages/auth/register/RegisterInstitution'));
const SuperAdminDashboard = lazy(() => import('./pages/dashboard/SuperAdminDashboard'));
const AdminDashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/dashboard/AdminDashboard'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const InstitutionList = lazy(() => import('./pages/superadmin/institutions/InstitutionList'));
const InstitutionForm = lazy(() => import('./pages/superadmin/institutions/InstitutionForm'));
const InstitutionView = lazy(() => import('./pages/superadmin/institutions/InstitutionView'));
const FestDashboard = lazy(() => import('./pages/dashboard/FestDashboard'));
const CoordinatorDashboard = lazy(() => import('./pages/dashboard/CoordinatorDashboard'));
const CoordinatorFestDashboard = lazy(() => import('./pages/coordinator/CoordinatorFestDashboard'));
const SuperAdminUserList = lazy(() => import('./pages/superadmin/users/UserList'));
const SuperAdminFestList = lazy(() => import('./pages/superadmin/fests/FestList'));
const SubscriptionPlanList = lazy(() => import('./pages/superadmin/subscriptions/PlanList'));
const SubscriptionPlanView = lazy(() => import('./pages/superadmin/subscriptions/PlanView'));
const InstitutionSubscription = lazy(() => import('./pages/admin/subscription/SubscriptionPlans'));
const AdminUserList = lazy(() => import('./pages/admin/users/UserList'));
const RolesAndPermissions = lazy(() => import('./pages/admin/roles/RolesAndPermissions'));
const SuperadminTemplatesPage = lazy(() => import('./pages/superadmin/templates/SuperadminTemplatesPage'));
const TemplateList = lazy(() => import('./pages/superadmin/certificates/TemplateList'));
const TemplateForm = lazy(() => import('./pages/superadmin/certificates/TemplateForm'));
const PosterTemplateList = lazy(() => import('./pages/superadmin/posters/PosterTemplateList'));
const PosterTemplateForm = lazy(() => import('./pages/superadmin/posters/PosterTemplateForm'));
const AdminCertificateTemplateList = lazy(() => import('./pages/admin/certificates/CertificateTemplateList'));
const AdminPosterTemplateList = lazy(() => import('./pages/admin/posters/PosterTemplateList'));
const AdminTemplates = lazy(() => import('./pages/admin/templates/Templates'));
const TemplateSelection = lazy(() => import('./pages/admin/certificates/TemplateSelection'));
const PosterTemplateSelection = lazy(() => import('./pages/admin/posters/PosterTemplateSelection'));
const PosterGallery = lazy(() => import('./pages/admin/posters/PosterGallery'));
const PosterGenerator = lazy(() => import('./pages/admin/posters/PosterGenerator'));
const ChampionshipPosterPage = lazy(() => import('./pages/admin/posters/ChampionshipPosterPage'));
const ChampionshipPosterOverviewPage = lazy(() => import('./pages/admin/posters/ChampionshipPosterOverviewPage'));
const JudgeAssignmentList = lazy(() => import('./pages/admin/judges/JudgeAssignmentList'));
const VolunteerAssignmentList = lazy(() => import('./pages/admin/volunteers/VolunteerAssignmentList'));
const AnnouncementForm = lazy(() => import('./pages/admin/volunteers/AnnouncementForm'));
const JudgeDash = lazy(() => import('./pages/judge/JudgeDashboard'));
const JudgeEvents = lazy(() => import('./pages/judge/JudgeEvents'));
const JudgeFestSelect = lazy(() => import('./pages/judge/JudgeFestSelect'));
const JudgeMarkEntry = lazy(() => import('./pages/judge/JudgeMarkEntry'));
const JudgeCodeLetters = lazy(() => import('./pages/judge/JudgeCodeLetters'));
const JudgeResults = lazy(() => import('./pages/judge/JudgeResults'));
const MarksEntry = lazy(() => import('./pages/judge/MarksEntry'));
const JudgeReport = lazy(() => import('./pages/judge/JudgeReport'));
const ResultView = lazy(() => import('./pages/admin/results/ResultView'));
const FestAllResults = lazy(() => import('./pages/admin/results/FestAllResults'));
const CertificateGenerator = lazy(() => import('./pages/admin/certificates/CertificateGenerator'));
const FestCertificatesTab = lazy(() => import('./pages/fest-admin/fests/components/FestCertificatesTab'));
const FestPostersTab = lazy(() => import('./pages/fest-admin/fests/components/FestPostersTab'));
const EventCertificatePage = lazy(() => import('./pages/admin/events/EventCertificatePage'));
const EventPosterPage = lazy(() => import('./pages/admin/events/EventPosterPage'));
const ReportDashboard = lazy(() => import('./pages/admin/reports/ReportDashboard'));
const ParticipantDashboard = lazy(() => import('./pages/participant/ParticipantDashboard'));
const VolunteerDashboard = lazy(() => import('./pages/volunteer/VolunteerDashboard'));
const VolunteerFestSelect = lazy(() => import('./pages/volunteer/VolunteerFestSelect'));
const VolunteerFestDashboard = lazy(() => import('./pages/volunteer/VolunteerFestDashboard'));
const VolunteerAssignments = lazy(() => import('./pages/volunteer/VolunteerAssignments'));
const VolunteerReport = lazy(() => import('./pages/volunteer/VolunteerReport'));
const TeamLeadDashboard = lazy(() => import('./pages/team-lead/TeamLeadDashboard'));
const MyFests = lazy(() => import('./pages/team-lead/MyFests'));
const TeamLeadEventAssignment = lazy(() => import('./pages/team-lead/TeamLeadEventAssignment'));
const TeamLeadReport = lazy(() => import('./pages/team-lead/TeamLeadReport'));
const MyEvents = lazy(() => import('./pages/participant/MyEvents'));
const UserFormPage = lazy(() => import('./components/UserForm'));
const EventList = lazy(() => import('./pages/fest-admin/events/EventList'));
const EventForm = lazy(() => import('./pages/fest-admin/events/EventForm'));
const EventDetail = lazy(() => import('./pages/fest-admin/events/EventDetail'));
const ParticipantList = lazy(() => import('./pages/fest-admin/participants/ParticipantList'));
const EventAssignment = lazy(() => import('./pages/fest-admin/participants/EventAssignment'));
const CodeLetters = lazy(() => import('./pages/fest-admin/participants/CodeLetters'));
const ParticipantForm = lazy(() => import('./pages/fest-admin/participants/ParticipantForm'));
const ParticipantDetail = lazy(() => import('./pages/fest-admin/participants/ParticipantDetail'));
const EventRegistration = lazy(() => import('./pages/fest-admin/participants/EventRegistration'));
const FestList = lazy(() => import('./pages/fest-admin/fests/FestList'));
const FestForm = lazy(() => import('./pages/fest-admin/fests/FestForm'));
const FestOverview = lazy(() => import('./pages/fest-admin/fests/FestOverview'));
const TeamList = lazy(() => import('./pages/fest-admin/teams/TeamList'));
const TeamForm = lazy(() => import('./pages/fest-admin/teams/TeamForm'));
const VenueList = lazy(() => import('./pages/fest-admin/venues/VenueList'));
const VenueForm = lazy(() => import('./pages/fest-admin/venues/VenueForm'));
const ChampionshipStandings = lazy(() => import('./pages/fest-admin/teams/ChampionshipStandings'));
const ChampionshipReport = lazy(() => import('./pages/fest-admin/reports/ChampionshipReport'));
const HouseDetails = lazy(() => import('./pages/fest-admin/teams/HouseDetails'));
const CategoryList = lazy(() => import('./pages/fest-admin/categories/CategoryList'));
const EventScheduler = lazy(() => import('./pages/fest-admin/schedule/EventScheduler'));
const EventManagement = lazy(() => import('./pages/coordinator/EventManagement'));
const ManualScoreEntry = lazy(() => import('./pages/coordinator/ManualScoreEntry'));
const CoordinatorFestSelect = lazy(() => import('./pages/coordinator/CoordinatorFestSelect'));
const CoordinatorReport = lazy(() => import('./pages/coordinator/CoordinatorReport'));
const Tabulation = lazy(() => import('./pages/fest-admin/tabulation/Tabulation'));
const CertificateCustomizationForm = lazy(() => import('./pages/fest-admin/fests/CertificateCustomizationForm'));
const FestManageWrapper = lazy(() => import('./navigation/FestManageWrapper'));
const MyProfile = lazy(() => import('./pages/profile/MyProfile'));
const CertPosterDemo = lazy(() => import('./pages/demo/CertPosterDemo'));

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <Spin size="large" />
  </div>
);

const CustomRoleDashboard = lazy(() => import('./pages/dashboard/CustomRoleDashboard'));
const CustomRoleFestSelect = lazy(() => import('./pages/dashboard/CustomRoleFestSelect'));

const RoleDashboard = () => {
  const { user } = useSelector((state: RootState) => state.User);
  if (user?.role === 'CUSTOM') return <CustomRoleDashboard />;
  return <AdminDashboard />;
};

const Unauthorized = () => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-background">
    <h2 className="text-2xl font-semibold text-foreground">
      403 - Unauthorized
    </h2>
    <p className="mt-2 text-muted-foreground">
      You do not have permission to access this page.
    </p>
  </div>
);

const EventEditRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to=".." relative="path" state={{ editId: id }} replace />;
};

const App: React.FC = () => {
  const { auth, user } = useSelector((state: RootState) => state.User);
  const { config, isDark } = useTheme();

  const getRedirectPath = () => {
    if (!auth || !user) return '/';
    switch (user.role) {
      case 'SUPER_ADMIN':
        return '/superadmin/dashboard';
      case 'INSTITUTION_ADMIN':
        return '/admin/dashboard';
      case 'FEST_ADMIN':
        return '/admin/dashboard';
      case 'ITEM_COORDINATOR':
        return '/coordinator/dashboard';
      case 'JUDGE':
        return '/judge/dashboard';
      case 'PARTICIPANT':
        return '/participant/dashboard';
      case 'VOLUNTEER':
        return '/volunteer/dashboard';
      case 'TEAM_LEAD':
        return '/team-lead/dashboard';
      case 'CUSTOM': {
        const slug = user.roleName ? user.roleName.toLowerCase().replace(/\s+/g, '-') : 'custom';
        return `/${slug}/dashboard`;
      }
      default:
        return '/admin/dashboard';
    }
  };

  return (
    <ConfigProvider
      getPopupContainer={() => document.body}
      theme={{
        algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: config.antPrimary,
          colorTextLightSolid: config.antPrimaryText,
          borderRadius: 12,
          borderRadiusLG: 16,
          fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        components: {
          Button: {
            primaryColor: config.antPrimaryText,
            colorPrimary: config.antPrimary,
          },
        },
      }}
    >
      <AntApp>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* Public Routes */}
            <Route
              path="/"
              element={
                auth ? (
                  <Navigate to={getRedirectPath()} replace />
                ) : (
                  <LandingPage />
                )
              }
            />
            <Route
              path="/login"
              element={
                auth ? (
                  <Navigate to={getRedirectPath()} replace />
                ) : (
                  <Login />
                )
              }
            />
            <Route
              path="/register"
              element={
                auth ? (
                  <Navigate to={getRedirectPath()} replace />
                ) : (
                  <RegisterInstitution />
                )
              }
            />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/demo/cert-poster" element={<CertPosterDemo />} />

            {/* Super Admin Routes */}
            <Route
              path="/superadmin/*"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AppLayout>
                    <Routes>
                      <Route path="dashboard" element={<SuperAdminDashboard />} />
                      <Route path="institutions" element={<InstitutionList />} />
                      <Route path="institutions/create" element={<InstitutionForm />} />
                      <Route path="institutions/:id" element={<InstitutionView />} />
                      <Route path="institutions/:id/edit" element={<InstitutionForm />} />
                      <Route path="fests" element={<SuperAdminFestList />} />
                      <Route path="subscriptions" element={<SubscriptionPlanList />} />
                      <Route path="subscriptions/:id/view" element={<SubscriptionPlanView />} />
                      <Route path="users" element={<SuperAdminUserList />} />
                      <Route path="users/create" element={<UserFormPage backPath="/superadmin/users" />} />
                      <Route path="users/:id" element={<UserFormPage backPath="/superadmin/users" />} />
                      <Route path="templates" element={<SuperadminTemplatesPage />} />
                      <Route path="certificate-templates/create" element={<TemplateForm />} />
                      <Route path="certificate-templates/:id/edit" element={<TemplateForm />} />
                      <Route path="poster-templates/create" element={<PosterTemplateForm />} />
                      <Route path="poster-templates/:id/edit" element={<PosterTemplateForm />} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="profile" element={<MyProfile />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />


            {/* Institution Admin Routes */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'INSTITUTION_ADMIN',
                    'FEST_ADMIN',
                    'CUSTOM',
                  ]}
                >
                  <AppLayout>
                    <Routes>
                      <Route path="dashboard" element={<RoleDashboard />} />
                      <Route path="subscription" element={<InstitutionSubscription />} />
                      <Route path="fests" element={<ProtectedRoute module="fests" action="view"><FestList /></ProtectedRoute>} />
                      <Route path="fests/create" element={<ProtectedRoute module="fests" action="create"><FestForm /></ProtectedRoute>} />
                      <Route path="fests/:id/view" element={<ProtectedRoute module="fests" action="view"><FestOverview /></ProtectedRoute>} />
                      <Route path="fests/:id/certificates/:templateId/customize" element={<ProtectedRoute module="fests" action="edit"><CertificateCustomizationForm /></ProtectedRoute>} />
                      <Route path="fests/:id/posters" element={<ProtectedRoute module="certificates" action="view"><FestPostersTab /></ProtectedRoute>} />
                      <Route path="fests/:id/posters/:eventId" element={<ProtectedRoute module="certificates" action="view"><EventPosterPage /></ProtectedRoute>} />
                      <Route path="fests/:id/certificates" element={<ProtectedRoute module="certificates" action="view"><FestCertificatesTab /></ProtectedRoute>} />
                      <Route path="fests/:id" element={<ProtectedRoute module="fests" action="edit"><FestForm /></ProtectedRoute>} />

                      {/* Fest Management Shell */}
                      <Route path="fests/:id/manage/*" element={
                        <FestManageWrapper>
                          <Routes>
                            <Route index element={<Navigate to="overview" replace />} />
                            <Route path="overview" element={<ProtectedRoute module="fests" action="view"><FestOverview /></ProtectedRoute>} />
                            <Route path="events" element={<ProtectedRoute module="events" action="view"><EventList /></ProtectedRoute>} />
                            <Route path="events/create" element={<ProtectedRoute module="events" action="create"><EventForm /></ProtectedRoute>} />
                            <Route path="events/:id/manage" element={<ProtectedRoute module="events" action="view"><EventManagement /></ProtectedRoute>} />
                            <Route path="events/:id/view" element={<ProtectedRoute module="events" action="view"><EventDetail /></ProtectedRoute>} />
                            <Route path="events/:eventId/scores" element={<ProtectedRoute module="participants" action="edit"><ManualScoreEntry /></ProtectedRoute>} />
                            <Route path="events/:eventId/certificates" element={<ProtectedRoute module="certificates" action="view"><EventCertificatePage /></ProtectedRoute>} />
                            <Route path="events/:eventId/posters" element={<ProtectedRoute module="certificates" action="view"><EventPosterPage /></ProtectedRoute>} />
                            <Route path="events/:id" element={<EventEditRedirect />} />
                            <Route path="categories" element={<ProtectedRoute module="categories" action="view"><CategoryList /></ProtectedRoute>} />
                            <Route path="participants" element={<ProtectedRoute module="participants" action="view"><ParticipantList /></ProtectedRoute>} />
                            <Route path="participants/create" element={<ProtectedRoute module="participants" action="create"><ParticipantForm /></ProtectedRoute>} />
                            <Route path="participants/event-assignment" element={<ProtectedRoute module="participants" action="edit"><EventAssignment /></ProtectedRoute>} />
                            <Route path="participants/code-letters" element={<ProtectedRoute module="participants" action="edit"><CodeLetters /></ProtectedRoute>} />
                            <Route path="participants/event-registration" element={<ProtectedRoute module="participants" action="edit"><EventRegistration /></ProtectedRoute>} />
                            <Route path="participants/:id/view" element={<ProtectedRoute module="participants" action="view"><ParticipantDetail /></ProtectedRoute>} />
                            <Route path="participants/:id" element={<ProtectedRoute module="participants" action="edit"><ParticipantForm /></ProtectedRoute>} />
                            <Route path="groups" element={<ProtectedRoute module="teams" action="view"><TeamList /></ProtectedRoute>} />
                            <Route path="groups/create" element={<ProtectedRoute module="teams" action="create"><TeamForm /></ProtectedRoute>} />
                            <Route path="groups/:id/view" element={<ProtectedRoute module="teams" action="view"><HouseDetails /></ProtectedRoute>} />
                            <Route path="groups/:id" element={<ProtectedRoute module="teams" action="edit"><TeamForm /></ProtectedRoute>} />
                            <Route path="venues" element={<ProtectedRoute module="venues" action="view"><VenueList /></ProtectedRoute>} />
                            <Route path="venues/create" element={<ProtectedRoute module="venues" action="create"><VenueForm /></ProtectedRoute>} />
                            <Route path="venues/:id" element={<ProtectedRoute module="venues" action="edit"><VenueForm /></ProtectedRoute>} />
                            <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                            <Route path="volunteers" element={<ProtectedRoute module="volunteers" action="view"><VolunteerAssignmentList /></ProtectedRoute>} />
                            <Route path="judges" element={<ProtectedRoute module="judges" action="view"><JudgeAssignmentList /></ProtectedRoute>} />
                            <Route path="announcements" element={<ProtectedRoute module="announcements" action="view"><AnnouncementForm /></ProtectedRoute>} />
                            <Route path="results" element={<ProtectedRoute module="results" action="view"><FestAllResults /></ProtectedRoute>} />
                            <Route path="tabulation" element={<ProtectedRoute module="results" action="edit"><Tabulation /></ProtectedRoute>} />
                            <Route path="championship" element={<ProtectedRoute module="championship" action="view"><ChampionshipStandings /></ProtectedRoute>} />
                            <Route path="championship-posters" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterOverviewPage /></ProtectedRoute>} />
                            <Route path="championship-poster/:type" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterPage /></ProtectedRoute>} />
                            <Route path="certificates" element={<ProtectedRoute module="certificates" action="view"><FestCertificatesTab /></ProtectedRoute>} />
                            <Route path="certificates/selection" element={<ProtectedRoute module="certificates" action="view"><TemplateSelection /></ProtectedRoute>} />
                            <Route path="certificates/:templateId/customize" element={<ProtectedRoute module="fests" action="edit"><CertificateCustomizationForm /></ProtectedRoute>} />
                            <Route path="posters" element={<ProtectedRoute module="certificates" action="view"><FestPostersTab /></ProtectedRoute>} />
                            <Route path="posters/gallery" element={<ProtectedRoute module="certificates" action="view"><PosterGallery /></ProtectedRoute>} />
                            <Route path="posters/templates" element={<ProtectedRoute module="certificates" action="view"><PosterTemplateSelection /></ProtectedRoute>} />
                            <Route path="posters/generate" element={<ProtectedRoute module="posters" action="view"><PosterGenerator /></ProtectedRoute>} />
                            <Route path="posters/:eventId" element={<ProtectedRoute module="certificates" action="view"><EventPosterPage /></ProtectedRoute>} />
                            <Route path="templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminTemplates /></ProtectedRoute>} />
                            <Route path="poster-templates/create" element={<ProtectedRoute module="poster_templates" action="create"><PosterTemplateForm /></ProtectedRoute>} />
                            <Route path="poster-templates/:id/edit" element={<ProtectedRoute module="poster_templates" action="edit"><PosterTemplateForm /></ProtectedRoute>} />
                            <Route path="certificate-templates/create" element={<ProtectedRoute module="certificate_templates" action="create"><TemplateForm /></ProtectedRoute>} />
                            <Route path="certificate-templates/:id/edit" element={<ProtectedRoute module="certificate_templates" action="edit"><TemplateForm /></ProtectedRoute>} />
                            <Route path="reports" element={<ProtectedRoute module="reports" action="view"><ReportDashboard /></ProtectedRoute>} />
                          </Routes>
                        </FestManageWrapper>
                      } />

                      <Route path="events" element={<ProtectedRoute module="events" action="view"><EventList /></ProtectedRoute>} />
                      <Route path="events/create" element={<ProtectedRoute module="events" action="create"><EventForm /></ProtectedRoute>} />
                      <Route path="events/:id/manage" element={<ProtectedRoute module="events" action="view"><EventManagement /></ProtectedRoute>} />
                      <Route path="events/:id/view" element={<ProtectedRoute module="events" action="view"><EventDetail /></ProtectedRoute>} />
                      <Route path="events/:eventId/scores" element={<ProtectedRoute module="participants" action="edit"><ManualScoreEntry /></ProtectedRoute>} />
                      <Route path="events/:eventId/certificates" element={<ProtectedRoute module="certificates" action="view"><EventCertificatePage /></ProtectedRoute>} />
                      <Route path="events/:eventId/posters" element={<ProtectedRoute module="certificates" action="view"><EventPosterPage /></ProtectedRoute>} />
                      <Route path="events/:id" element={<EventEditRedirect />} />
                      <Route path="categories" element={<ProtectedRoute module="categories" action="view"><CategoryList /></ProtectedRoute>} />
                      <Route path="participants" element={<ProtectedRoute module="participants" action="view"><ParticipantList /></ProtectedRoute>} />
                      <Route path="participants/create" element={<ProtectedRoute module="participants" action="create"><ParticipantForm /></ProtectedRoute>} />
                      <Route path="participants/event-registration" element={<ProtectedRoute module="participants" action="edit"><EventRegistration /></ProtectedRoute>} />
                      <Route path="participants/:id/view" element={<ProtectedRoute module="participants" action="view"><ParticipantDetail /></ProtectedRoute>} />
                      <Route path="participants/:id" element={<ProtectedRoute module="participants" action="edit"><ParticipantForm /></ProtectedRoute>} />
                      <Route path="teams" element={<ProtectedRoute module="teams" action="view"><TeamList /></ProtectedRoute>} />
                      <Route path="teams/create" element={<ProtectedRoute module="teams" action="create"><TeamForm /></ProtectedRoute>} />
                      <Route path="teams/:id/view" element={<ProtectedRoute module="teams" action="view"><HouseDetails /></ProtectedRoute>} />
                      <Route path="teams/:id" element={<ProtectedRoute module="teams" action="edit"><TeamForm /></ProtectedRoute>} />
                      <Route path="judges/assignments" element={<ProtectedRoute module="judges" action="view"><JudgeAssignmentList /></ProtectedRoute>} />
                      <Route path="volunteers/assignments" element={<ProtectedRoute module="volunteers" action="view"><VolunteerAssignmentList /></ProtectedRoute>} />
                      <Route path="announcements" element={<ProtectedRoute module="announcements" action="view"><AnnouncementForm /></ProtectedRoute>} />
                      <Route path="groups" element={<ProtectedRoute module="teams" action="view"><TeamList /></ProtectedRoute>} />
                      <Route path="groups/create" element={<ProtectedRoute module="teams" action="create"><TeamForm /></ProtectedRoute>} />
                      <Route path="groups/:id/view" element={<ProtectedRoute module="teams" action="view"><HouseDetails /></ProtectedRoute>} />
                      <Route path="groups/:id" element={<ProtectedRoute module="teams" action="edit"><TeamForm /></ProtectedRoute>} />
                      <Route path="venues" element={<ProtectedRoute module="venues" action="view"><VenueList /></ProtectedRoute>} />
                      <Route path="venues/create" element={<ProtectedRoute module="venues" action="create"><VenueForm /></ProtectedRoute>} />
                      <Route path="venues/:id" element={<ProtectedRoute module="venues" action="edit"><VenueForm /></ProtectedRoute>} />
                      <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                      <Route path="results" element={<ProtectedRoute module="results" action="view"><ResultView /></ProtectedRoute>} />
                      <Route path="results/championship" element={<ProtectedRoute module="championship" action="view"><ChampionshipStandings /></ProtectedRoute>} />
                      <Route path="results/championship-posters" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterOverviewPage /></ProtectedRoute>} />
                      <Route path="results/championship-poster/:type" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterPage /></ProtectedRoute>} />
                      <Route path="posters/championship/:type" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterPage /></ProtectedRoute>} />
                      <Route path="reports/championship" element={<ProtectedRoute module="reports" action="view"><ChampionshipReport /></ProtectedRoute>} />
                      <Route path="certificates" element={<ProtectedRoute module="certificates" action="view"><CertificateGenerator /></ProtectedRoute>} />
                      <Route path="certificates/selection" element={<ProtectedRoute module="certificates" action="view"><TemplateSelection /></ProtectedRoute>} />
                      <Route path="posters" element={<ProtectedRoute module="certificates" action="view"><PosterGallery /></ProtectedRoute>} />
                      <Route path="posters/templates" element={<ProtectedRoute module="certificates" action="view"><PosterTemplateSelection /></ProtectedRoute>} />
                      <Route path="posters/generate" element={<ProtectedRoute module="posters" action="view"><PosterGenerator /></ProtectedRoute>} />
                      <Route path="reports" element={<ProtectedRoute module="reports" action="view"><ReportDashboard /></ProtectedRoute>} />
                      <Route path="users" element={<ProtectedRoute module="users" action="view"><AdminUserList /></ProtectedRoute>} />
                      <Route path="roles" element={<ProtectedRoute module="users" action="view"><RolesAndPermissions /></ProtectedRoute>} />
                      <Route path="users/create" element={<ProtectedRoute module="users" action="create"><UserFormPage backPath="/admin/users" /></ProtectedRoute>} />
                      <Route path="users/:id" element={<ProtectedRoute module="users" action="edit"><UserFormPage backPath="/admin/users" /></ProtectedRoute>} />
                      <Route path="settings" element={<ProtectedRoute module="settings" action="view"><Settings /></ProtectedRoute>} />
                      <Route path="profile" element={<MyProfile />} />

                      {/* Content Management — Institution Admin only */}
                      <Route path="templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminTemplates /></ProtectedRoute>} />
                      <Route path="certificate-templates" element={<ProtectedRoute module="certificate_templates" action="view"><AdminCertificateTemplateList /></ProtectedRoute>} />
                      <Route path="certificate-templates/create" element={<ProtectedRoute module="certificate_templates" action="create"><TemplateForm /></ProtectedRoute>} />
                      <Route path="certificate-templates/:id/edit" element={<ProtectedRoute module="certificate_templates" action="edit"><TemplateForm /></ProtectedRoute>} />
                      <Route path="poster-templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminPosterTemplateList /></ProtectedRoute>} />
                      <Route path="poster-templates/create" element={<ProtectedRoute module="poster_templates" action="create"><PosterTemplateForm /></ProtectedRoute>} />
                      <Route path="poster-templates/:id/edit" element={<ProtectedRoute module="poster_templates" action="edit"><PosterTemplateForm /></ProtectedRoute>} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Fest Admin Routes */}
            <Route
              path="/fest-admin/*"
              element={
                <ProtectedRoute allowedRoles={['FEST_ADMIN', 'INSTITUTION_ADMIN', 'SUPER_ADMIN']}>
                  <AppLayout>
                    <Routes>
                      <Route path="dashboard" element={<FestDashboard />} />
                      <Route path="fests" element={<ProtectedRoute module="fests" action="view"><FestList /></ProtectedRoute>} />
                      <Route path="fests/create" element={<ProtectedRoute module="fests" action="create"><FestForm /></ProtectedRoute>} />
                      <Route path="fests/:id/view" element={<ProtectedRoute module="fests" action="view"><FestOverview /></ProtectedRoute>} />
                      <Route path="fests/:id/certificates/:templateId/customize" element={<ProtectedRoute module="fests" action="edit"><CertificateCustomizationForm /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminTemplates /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/poster-templates/create" element={<ProtectedRoute module="poster_templates" action="create"><PosterTemplateForm /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/poster-templates/:templateId/edit" element={<ProtectedRoute module="poster_templates" action="edit"><PosterTemplateForm /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/certificate-templates/create" element={<ProtectedRoute module="certificate_templates" action="create"><TemplateForm /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/certificate-templates/:templateId/edit" element={<ProtectedRoute module="certificate_templates" action="edit"><TemplateForm /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/posters" element={<ProtectedRoute module="certificates" action="view"><FestPostersTab /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/certificates" element={<ProtectedRoute module="certificates" action="view"><FestCertificatesTab /></ProtectedRoute>} />
                      <Route path="fests/:id/manage/certificates/:templateId/customize" element={<ProtectedRoute module="fests" action="edit"><CertificateCustomizationForm /></ProtectedRoute>} />
                      <Route path="fests/:id" element={<ProtectedRoute module="fests" action="edit"><FestForm /></ProtectedRoute>} />
                      <Route path="events" element={<ProtectedRoute module="events" action="view"><EventList /></ProtectedRoute>} />
                      <Route path="events/create" element={<ProtectedRoute module="events" action="edit"><EventForm /></ProtectedRoute>} />
                      <Route path="events/:id" element={<EventEditRedirect />} />
                      <Route path="categories" element={<ProtectedRoute module="categories" action="view"><CategoryList /></ProtectedRoute>} />
                      <Route path="participants" element={<ProtectedRoute module="participants" action="view"><ParticipantList /></ProtectedRoute>} />
                      <Route path="participants/create" element={<ProtectedRoute module="participants" action="edit"><ParticipantForm /></ProtectedRoute>} />
                      <Route path="participants/event-registration" element={<ProtectedRoute module="participants" action="edit"><EventRegistration /></ProtectedRoute>} />
                      <Route path="participants/:id" element={<ProtectedRoute module="participants" action="edit"><ParticipantForm /></ProtectedRoute>} />
                      <Route path="teams" element={<ProtectedRoute module="teams" action="view"><TeamList /></ProtectedRoute>} />
                      <Route path="teams/create" element={<ProtectedRoute module="teams" action="create"><TeamForm /></ProtectedRoute>} />
                      <Route path="teams/:id/view" element={<ProtectedRoute module="teams" action="view"><HouseDetails /></ProtectedRoute>} />
                      <Route path="teams/:id" element={<ProtectedRoute module="teams" action="edit"><TeamForm /></ProtectedRoute>} />
                      <Route path="venues" element={<ProtectedRoute module="venues" action="view"><VenueList /></ProtectedRoute>} />
                      <Route path="venues/create" element={<ProtectedRoute module="venues" action="create"><VenueForm /></ProtectedRoute>} />
                      <Route path="venues/:id" element={<ProtectedRoute module="venues" action="edit"><VenueForm /></ProtectedRoute>} />
                      <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                      <Route path="championship" element={<ProtectedRoute module="results" action="view"><ChampionshipStandings /></ProtectedRoute>} />
                      <Route path="championship-posters" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterOverviewPage /></ProtectedRoute>} />
                      <Route path="championship-poster/:type" element={<ProtectedRoute module="posters" action="view"><ChampionshipPosterPage /></ProtectedRoute>} />
                      <Route path="users" element={<ProtectedRoute module="users" action="view"><AdminUserList /></ProtectedRoute>} />
                      <Route path="users/create" element={<ProtectedRoute module="users" action="create"><UserFormPage backPath="/fest-admin/users" /></ProtectedRoute>} />
                      <Route path="users/:id" element={<ProtectedRoute module="users" action="edit"><UserFormPage backPath="/fest-admin/users" /></ProtectedRoute>} />
                      <Route path="settings" element={<ProtectedRoute module="settings" action="view"><Settings /></ProtectedRoute>} />
                      <Route path="profile" element={<MyProfile />} />

                      {/* Template Management */}
                      <Route path="templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminTemplates /></ProtectedRoute>} />
                      <Route path="certificate-templates" element={<ProtectedRoute module="certificate_templates" action="view"><AdminCertificateTemplateList /></ProtectedRoute>} />
                      <Route path="certificate-templates/create" element={<ProtectedRoute module="certificate_templates" action="create"><TemplateForm /></ProtectedRoute>} />
                      <Route path="certificate-templates/:id/edit" element={<ProtectedRoute module="certificate_templates" action="edit"><TemplateForm /></ProtectedRoute>} />
                      <Route path="poster-templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminPosterTemplateList /></ProtectedRoute>} />
                      <Route path="poster-templates/create" element={<ProtectedRoute module="poster_templates" action="create"><PosterTemplateForm /></ProtectedRoute>} />
                      <Route path="poster-templates/:id/edit" element={<ProtectedRoute module="poster_templates" action="edit"><PosterTemplateForm /></ProtectedRoute>} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Item Coordinator Routes */}
            <Route
              path="/coordinator/*"
              element={
                <ProtectedRoute allowedRoles={['ITEM_COORDINATOR']}>
                  <AppLayout>
                    <Routes>
                      <Route index element={<Navigate to="/coordinator/dashboard" replace />} />
                      <Route path="dashboard" element={<CoordinatorDashboard />} />
                      <Route path="fests" element={<CoordinatorFestSelect />} />
                      {/* Fest-specific routes — guarded by fest selection */}
                      <Route path="fests/:festId/*" element={
                        <CoordinatorFestGuard>
                          <Routes>
                            <Route path="dashboard" element={<CoordinatorFestDashboard />} />
                            <Route path="events" element={<ProtectedRoute module="events" action="view"><EventList /></ProtectedRoute>} />
                            <Route path="events/:id/manage" element={<ProtectedRoute module="events" action="view"><EventManagement /></ProtectedRoute>} />
                            <Route path="events/:eventId/scores" element={<ProtectedRoute module="participants" action="edit"><ManualScoreEntry /></ProtectedRoute>} />
                            <Route path="events/:id" element={<EventEditRedirect />} />
                            <Route path="participants" element={<ProtectedRoute module="participants" action="view"><ParticipantList /></ProtectedRoute>} />
                            <Route path="participants/create" element={<ProtectedRoute module="participants" action="create"><ParticipantForm /></ProtectedRoute>} />
                            <Route path="participants/event-registration" element={<ProtectedRoute module="participants" action="edit"><EventAssignment /></ProtectedRoute>} />
                            <Route path="participants/code-letters" element={<ProtectedRoute module="participants" action="edit"><CodeLetters /></ProtectedRoute>} />
                            <Route path="participants/:id/view" element={<ProtectedRoute module="participants" action="view"><ParticipantDetail /></ProtectedRoute>} />
                            <Route path="participants/:id" element={<ProtectedRoute module="participants" action="edit"><ParticipantForm /></ProtectedRoute>} />
                            <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                            <Route path="announcements" element={<ProtectedRoute module="announcements" action="view"><AnnouncementForm /></ProtectedRoute>} />
                            <Route path="volunteers/assignments" element={<ProtectedRoute module="volunteers" action="view"><VolunteerAssignmentList /></ProtectedRoute>} />
                            <Route path="judges/assignments" element={<ProtectedRoute module="judges" action="view"><JudgeAssignmentList /></ProtectedRoute>} />
                            <Route path="results" element={<ProtectedRoute module="results" action="view"><FestAllResults /></ProtectedRoute>} />
                            <Route path="posters/:eventId" element={<ProtectedRoute module="certificates" action="view"><EventPosterPage /></ProtectedRoute>} />
                            <Route path="championship" element={<ProtectedRoute module="championship" action="view"><ChampionshipStandings /></ProtectedRoute>} />
                            <Route path="reports" element={<CoordinatorReport />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="profile" element={<MyProfile />} />
                            <Route path="*" element={<Navigate to="dashboard" replace />} />
                          </Routes>
                        </CoordinatorFestGuard>
                      } />
                      <Route path="settings" element={<Settings />} />
                      <Route path="profile" element={<MyProfile />} />
                      <Route path="*" element={<Navigate to="/coordinator/dashboard" replace />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Judge Routes */}
            <Route
              path="/judge/*"
              element={
                <ProtectedRoute allowedRoles={['JUDGE']}>
                  <AppLayout>
                    <Routes>
                      <Route index element={<Navigate to="/judge/dashboard" replace />} />
                      <Route path="fests" element={<JudgeFestSelect />} />
                      <Route path="announcements" element={<AnnouncementForm />} />
                      <Route path="dashboard" element={<JudgeDash />} />
                      <Route path="events" element={<JudgeEvents />} />
                      <Route path="mark-entry" element={<JudgeMarkEntry />} />
                      <Route path="code-letters" element={<JudgeCodeLetters />} />
                      <Route path="results" element={<JudgeResults />} />
                      <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                      <Route path="championship" element={<ChampionshipStandings />} />
                      <Route path="reports" element={<JudgeReport />} />
                      <Route path="score/:eventId" element={<ProtectedRoute module="scoring" action="edit"><MarksEntry /></ProtectedRoute>} />
                      <Route path="settings" element={<ProtectedRoute module="settings" action="view"><Settings /></ProtectedRoute>} />
                      <Route path="profile" element={<MyProfile />} />
                      <Route path="*" element={<Navigate to="/judge/dashboard" replace />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />


            {/* Participant Routes */}
            <Route
              path="/participant/*"
              element={
                <ProtectedRoute allowedRoles={['PARTICIPANT']}>
                  <AppLayout>
                    <Routes>
                      <Route index element={<Navigate to="/participant/dashboard" replace />} />
                      <Route path="dashboard" element={<ParticipantDashboard />} />
                      <Route path="events" element={<MyEvents />} />
                      <Route path="results" element={<div className="p-8 text-center text-muted-foreground">Results will be published after the completion of events.</div>} />
                      <Route path="certificates" element={<div className="p-8 text-center text-muted-foreground">Certificates will be available for download after results are published.</div>} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="profile" element={<MyProfile />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Volunteer Routes */}
            <Route
              path="/volunteer/*"
              element={
                <ProtectedRoute allowedRoles={['VOLUNTEER']}>
                  <AppLayout>
                    <Routes>
                      <Route index element={<Navigate to="/volunteer/dashboard" replace />} />
                      <Route path="dashboard" element={<VolunteerDashboard />} />
                      <Route path="fests" element={<VolunteerFestSelect />} />
                      <Route path="fests/:festId/*" element={
                        <VolunteerFestGuard>
                          <Routes>
                            <Route path="dashboard" element={<VolunteerFestDashboard />} />
                            <Route path="assignments" element={<VolunteerAssignments />} />
                            <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                            <Route path="announcements" element={<AnnouncementForm />} />
                            <Route path="championship" element={<ChampionshipStandings />} />
                            <Route path="reports" element={<VolunteerReport />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="profile" element={<MyProfile />} />
                            <Route path="*" element={<Navigate to="dashboard" replace />} />
                          </Routes>
                        </VolunteerFestGuard>
                      } />
                      <Route path="settings" element={<Settings />} />
                      <Route path="profile" element={<MyProfile />} />
                      <Route path="*" element={<Navigate to="/volunteer/dashboard" replace />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Team Lead Routes */}
            <Route
              path="/team-lead/*"
              element={
                <ProtectedRoute allowedRoles={['TEAM_LEAD']}>
                  <AppLayout>
                    <TeamLeadFestGuard>
                      <Routes>
                        <Route index element={<Navigate to="/team-lead/dashboard" replace />} />
                        <Route path="dashboard" element={<TeamLeadDashboard />} />
                        <Route path="my-fests" element={<MyFests />} />
                        <Route path="participants" element={<ParticipantList />} />
                        <Route path="participants/create" element={<ParticipantForm />} />
                        <Route path="participants/event-registration" element={<ProtectedRoute module="participants" action="edit"><EventRegistration /></ProtectedRoute>} />
                        <Route path="participants/:id/view" element={<ParticipantDetail />} />
                        <Route path="participants/:id" element={<ParticipantForm />} />
                        <Route path="event-registration" element={<TeamLeadEventAssignment />} />
                        <Route path="schedule" element={<ProtectedRoute module="schedules" action="view"><EventScheduler /></ProtectedRoute>} />
                        <Route path="results" element={<FestAllResults />} />
                        <Route path="posters/:eventId" element={<EventPosterPage />} />
                        <Route path="championship" element={<ChampionshipStandings />} />
                        <Route path="reports" element={<TeamLeadReport />} />
                        <Route path="announcements" element={<AnnouncementForm />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="profile" element={<MyProfile />} />
                        <Route path="*" element={<Navigate to="/team-lead/dashboard" replace />} />
                      </Routes>
                    </TeamLeadFestGuard>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            {/* Custom Role Routes — e.g. /creator/*, /event-manager/* */}
            <Route
              path="/:customRole/*"
              element={
                <ProtectedRoute allowedRoles={['CUSTOM']}>
                  <AppLayout>
                    <Routes>
                      {/* Non-fest-scoped pages — accessible without selecting a fest */}
                      <Route path="dashboard" element={<CustomRoleDashboard />} />
                      <Route path="fests" element={<CustomRoleFestSelect />} />
                      <Route path="templates" element={<ProtectedRoute module="certificates" action="view"><AdminTemplates /></ProtectedRoute>} />
                      <Route path="certificate-templates" element={<ProtectedRoute module="certificates" action="view"><AdminCertificateTemplateList /></ProtectedRoute>} />
                      <Route path="certificate-templates/create" element={<ProtectedRoute module="certificates" action="create"><TemplateForm /></ProtectedRoute>} />
                      <Route path="certificate-templates/:id/edit" element={<ProtectedRoute module="certificates" action="edit"><TemplateForm /></ProtectedRoute>} />
                      <Route path="poster-templates" element={<ProtectedRoute module="certificates" action="view"><AdminPosterTemplateList /></ProtectedRoute>} />
                      <Route path="poster-templates/create" element={<ProtectedRoute module="certificates" action="create"><PosterTemplateForm /></ProtectedRoute>} />
                      <Route path="poster-templates/:id/edit" element={<ProtectedRoute module="certificates" action="edit"><PosterTemplateForm /></ProtectedRoute>} />
                      <Route path="users" element={<ProtectedRoute module="users" action="view"><AdminUserList /></ProtectedRoute>} />
                      <Route path="reports" element={<ProtectedRoute module="reports" action="view"><ReportDashboard /></ProtectedRoute>} />
                      <Route path="settings" element={<ProtectedRoute module="settings" action="view"><Settings /></ProtectedRoute>} />
                      <Route path="profile" element={<MyProfile />} />

                      {/* Fest-scoped pages — require selecting a fest first */}
                      <Route path="fests/:festId/*" element={
                        <CustomRoleFestGuard>
                          <Routes>
                            <Route path="dashboard" element={<CustomRoleDashboard />} />
                            <Route path="events" element={<ProtectedRoute module="events" action="view"><EventList /></ProtectedRoute>} />
                            <Route path="categories" element={<ProtectedRoute module="categories" action="view"><CategoryList /></ProtectedRoute>} />
                            <Route path="participants" element={<ProtectedRoute module="participants" action="view"><ParticipantList /></ProtectedRoute>} />
                            <Route path="teams" element={<ProtectedRoute module="teams" action="view"><TeamList /></ProtectedRoute>} />
                            <Route path="venues" element={<ProtectedRoute module="venues" action="view"><VenueList /></ProtectedRoute>} />
                            <Route path="results" element={<ProtectedRoute module="results" action="view"><ResultView /></ProtectedRoute>} />
                            <Route path="judges/assignments" element={<ProtectedRoute module="judges" action="view"><JudgeAssignmentList /></ProtectedRoute>} />
                            <Route path="volunteers/assignments" element={<ProtectedRoute module="volunteers" action="view"><VolunteerAssignmentList /></ProtectedRoute>} />
                            <Route path="announcements" element={<ProtectedRoute module="announcements" action="view"><AnnouncementForm /></ProtectedRoute>} />
                            <Route path="templates" element={<ProtectedRoute module="poster_templates" action="view"><AdminTemplates /></ProtectedRoute>} />
                            <Route path="poster-templates/create" element={<ProtectedRoute module="poster_templates" action="create"><PosterTemplateForm /></ProtectedRoute>} />
                            <Route path="poster-templates/:templateId/edit" element={<ProtectedRoute module="poster_templates" action="edit"><PosterTemplateForm /></ProtectedRoute>} />
                            <Route path="certificate-templates/create" element={<ProtectedRoute module="certificate_templates" action="create"><TemplateForm /></ProtectedRoute>} />
                            <Route path="certificate-templates/:templateId/edit" element={<ProtectedRoute module="certificate_templates" action="edit"><TemplateForm /></ProtectedRoute>} />
                            <Route index element={<Navigate to="dashboard" replace />} />
                            <Route path="*" element={<Navigate to="dashboard" replace />} />
                          </Routes>
                        </CustomRoleFestGuard>
                      } />

                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="*" element={<Navigate to="dashboard" replace />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
